// File security validation utilities
export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Magic number signatures for common file types
const FILE_SIGNATURES = {
  'image/jpeg': [0xFF, 0xD8, 0xFF],
  'image/png': [0x89, 0x50, 0x4E, 0x47],
  'image/gif': [0x47, 0x49, 0x46, 0x38],
  'image/webp': [0x52, 0x49, 0x46, 0x46],
  'video/mp4': [0x00, 0x00, 0x00, 0x20, 0x66, 0x74, 0x79, 0x70], // Partial signature
  'application/pdf': [0x25, 0x50, 0x44, 0x46],
};

// File size limits per type (in bytes)
const FILE_SIZE_LIMITS = {
  'image/jpeg': 10 * 1024 * 1024, // 10MB
  'image/png': 10 * 1024 * 1024,  // 10MB
  'image/gif': 5 * 1024 * 1024,   // 5MB
  'image/webp': 8 * 1024 * 1024,  // 8MB
  'video/mp4': 100 * 1024 * 1024, // 100MB
  'video/webm': 100 * 1024 * 1024, // 100MB
  'application/pdf': 25 * 1024 * 1024, // 25MB
};

// Dangerous extensions that should never be allowed
const DANGEROUS_EXTENSIONS = [
  'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'js', 'jar',
  'jsp', 'php', 'asp', 'aspx', 'sh', 'py', 'rb', 'pl', 'cgi'
];

export const validateFileContent = async (file: File): Promise<FileValidationResult> => {
  const result: FileValidationResult = {
    isValid: true,
    errors: [],
    warnings: []
  };

  try {
    // 1. Validate file extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension) {
      result.errors.push('File must have an extension');
      result.isValid = false;
      return result;
    }

    if (DANGEROUS_EXTENSIONS.includes(extension)) {
      result.errors.push(`File extension '${extension}' is not allowed for security reasons`);
      result.isValid = false;
      return result;
    }

    // 2. Validate MIME type matches extension
    const expectedMimeTypes = getExpectedMimeTypes(extension);
    if (expectedMimeTypes.length > 0 && !expectedMimeTypes.includes(file.type)) {
      result.errors.push(`File type mismatch: expected ${expectedMimeTypes.join(' or ')}, got ${file.type}`);
      result.isValid = false;
    }

    // 3. Validate file size
    const sizeLimit = FILE_SIZE_LIMITS[file.type as keyof typeof FILE_SIZE_LIMITS];
    if (sizeLimit && file.size > sizeLimit) {
      result.errors.push(`File size exceeds limit of ${(sizeLimit / (1024 * 1024)).toFixed(1)}MB`);
      result.isValid = false;
    }

    // 4. Validate magic numbers (file signature)
    const signature = FILE_SIGNATURES[file.type as keyof typeof FILE_SIGNATURES];
    if (signature) {
      const isValidSignature = await validateFileSignature(file, signature);
      if (!isValidSignature) {
        result.errors.push('File content does not match its declared type (invalid file signature)');
        result.isValid = false;
      }
    }

    // 5. Check for suspicious filenames
    if (hasSuspiciousFilename(file.name)) {
      result.warnings.push('Filename contains potentially suspicious characters');
    }

    // 6. Additional security checks for images
    if (file.type.startsWith('image/')) {
      const imageValidation = await validateImageSecurity(file);
      result.errors.push(...imageValidation.errors);
      result.warnings.push(...imageValidation.warnings);
      if (imageValidation.errors.length > 0) {
        result.isValid = false;
      }
    }

  } catch (error) {
    result.errors.push('Failed to validate file: ' + (error as Error).message);
    result.isValid = false;
  }

  return result;
};

const getExpectedMimeTypes = (extension: string): string[] => {
  const mimeMap: Record<string, string[]> = {
    'jpg': ['image/jpeg'],
    'jpeg': ['image/jpeg'],
    'png': ['image/png'],
    'gif': ['image/gif'],
    'webp': ['image/webp'],
    'mp4': ['video/mp4'],
    'webm': ['video/webm'],
    'pdf': ['application/pdf'],
  };
  return mimeMap[extension] || [];
};

const validateFileSignature = async (file: File, expectedSignature: number[]): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const uint8Array = new Uint8Array(arrayBuffer);

      // Special-case MP4: the "ftyp" box is typically near the start but the size bytes can vary,
      // so a fixed byte-for-byte signature is unreliable.
      if (file.type === 'video/mp4') {
        const f = 'ftyp';
        const f0 = f.charCodeAt(0);
        const f1 = f.charCodeAt(1);
        const f2 = f.charCodeAt(2);
        const f3 = f.charCodeAt(3);

        // Search for "ftyp" within the first 64 bytes.
        const maxOffset = Math.min(uint8Array.length - 4, 64);
        for (let i = 0; i <= maxOffset; i++) {
          if (
            uint8Array[i] === f0 &&
            uint8Array[i + 1] === f1 &&
            uint8Array[i + 2] === f2 &&
            uint8Array[i + 3] === f3
          ) {
            resolve(true);
            return;
          }
        }

        resolve(false);
        return;
      }

      // Default strict signature compare for other types
      if (uint8Array.length < expectedSignature.length) {
        resolve(false);
        return;
      }

      for (let i = 0; i < expectedSignature.length; i++) {
        if (uint8Array[i] !== expectedSignature[i]) {
          resolve(false);
          return;
        }
      }

      resolve(true);
    };

    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(
      file.slice(
        0,
        // Read enough bytes for signature checks (MP4 may need a wider window)
        Math.max(64, ...Object.values(FILE_SIGNATURES).map((sig) => sig.length))
      )
    );
  });
};

const hasSuspiciousFilename = (filename: string): boolean => {
  const suspiciousPatterns = [
    /\.\./,           // Directory traversal
    /[<>:"|?*]/,      // Windows reserved characters
    /^\./,            // Hidden files
    /\.php\./,        // Double extensions
    /\.exe\./,        // Executable disguised
    /\0/,             // Null bytes
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(filename));
};

const validateImageSecurity = async (file: File): Promise<{ errors: string[], warnings: string[] }> => {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    // Create image element to validate
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        // Check image dimensions
        if (img.width > 10000 || img.height > 10000) {
          errors.push('Image dimensions are too large (max 10000x10000)');
        }

        if (img.width * img.height > 50000000) { // 50 megapixels
          warnings.push('Image has very high resolution, consider optimizing');
        }

        // Check for unusual aspect ratios that might indicate malicious content
        const aspectRatio = img.width / img.height;
        if (aspectRatio > 20 || aspectRatio < 0.05) {
          warnings.push('Unusual image aspect ratio detected');
        }

        resolve();
      };
      img.onerror = () => reject(new Error('Invalid image file'));
      img.src = URL.createObjectURL(file);
    });

    // Clean up
    URL.revokeObjectURL(img.src);

  } catch (error) {
    errors.push('Failed to validate image: ' + (error as Error).message);
  }

  return { errors, warnings };
};

export const sanitizeFilename = (filename: string): string => {
  // Remove dangerous characters and normalize
  return filename
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace special chars with underscore
    .replace(/\.+/g, '.') // Collapse multiple dots
    .replace(/^\./, '') // Remove leading dots
    .toLowerCase()
    .substring(0, 100); // Limit length
};

export const getSecureContentType = (file: File): string => {
  // Always return the detected MIME type, never trust user input
  const extension = file.name.split('.').pop()?.toLowerCase();
  const trustedTypes: Record<string, string> = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'mp4': 'video/mp4',
    'webm': 'video/webm',
  };
  
  return trustedTypes[extension || ''] || 'application/octet-stream';
};